import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { DataList, LogLines, StatusPill, type DataListColumn, type StatusTone } from "@ozpanel/kit";
import { Section, StateView, formatBytes, useAuth } from "@ozpanel/ui";

// tinc measured from the RUNNING daemon — GET /tinc/health (every network, active or not) and
// GET /tinc/networks/{net}/status (one network's node, edge, subnet and connection tables, read
// through tinc's control socket). Each table the daemon did not give arrives as null with a
// *_error reason, and is shown as "not measured — <reason>", never as an empty table: an empty
// node list on a mesh reads as "no peers", which is a fault, while an unanswered socket is an
// absence of information.

export interface UnitState {
  unit: string;
  state: string; // active | inactive | failed | activating | … | unknown
  sub_state?: string;
  since?: string;
  state_error?: string;
}

interface NetworkHealth extends UnitState {
  network: string;
  kind: string;
  nodes: number | null;
  reachable: number | null;
  unreachable: string[] | null;
  reach_error?: string;
  connections: number | null;
  connections_error?: string;
}

interface TincHealth {
  status: string;
  checks: { name: string; ok: boolean; detail?: string; unknown?: boolean }[] | null;
  networks: NetworkHealth[] | null;
  networks_error?: string;
  summary?: { total: number; active: number; inactive: number; unknown: number };
}

interface TincNode {
  name: string;
  host: string;
  port: string;
  distance: string;
  pmtu: string;
  status: string;
  self: boolean;
  rtt_ms?: number;
  rx_packets: number;
  rx_bytes: number;
  tx_packets: number;
  tx_bytes: number;
  reachable: boolean;
  id?: string;
  via?: string;
  nexthop?: string;
  protocol_minor: number;
  valid_key: boolean;
  udp_confirmed: boolean;
  indirect: boolean;
}

interface TincEdge { from: string; to: string; host: string; port: string; local_host?: string; local_port?: string; weight: number }
interface TincSubnet { subnet: string; owner: string }
interface TincConnection { node: string; host: string; port: string; socket: number; encrypted: boolean; mst: boolean }

interface NetworkStatus extends UnitState {
  network: string;
  kind: string;
  self?: string;
  nodes: TincNode[] | null;
  nodes_error?: string;
  edges: TincEdge[] | null;
  edges_error?: string;
  subnets: TincSubnet[] | null;
  subnets_error?: string;
  connections: TincConnection[] | null;
  connections_error?: string;
  traffic: { rx_packets: number; rx_bytes: number; tx_packets: number; tx_bytes: number } | null;
  counts: Record<string, number | null>;
}

export function unitTone(state: string): StatusTone {
  switch (state) {
    case "active":
    case "reloading":
      return "ok";
    case "failed":
      return "bad";
    case "unknown":
      return "neutral";
    default:
      return "warn"; // inactive, activating, deactivating
  }
}

export function UnitPill({ u }: { u: UnitState }) {
  return (
    <span title={u.state_error || (u.sub_state ? `${u.state} (${u.sub_state})${u.since ? ` since ${u.since}` : ""}` : u.state)}>
      <StatusPill tone={unitTone(u.state)}>{u.state === "unknown" ? "state unknown" : u.state}</StatusPill>
    </span>
  );
}

export function NotMeasured({ why }: { why?: string }) {
  return <div className="px-4 py-3 text-[12.5px] text-warn">Not measured — {why || "tinc did not answer"}</div>;
}

const n = (v: number | null | undefined) => (v === null || v === undefined ? "?" : String(v));

/** Every configured network with its unit state — inactive and failed ones listed, not dropped. */
export function TincHealthStrip({ onOpen }: { onOpen?: (net: string) => void }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["tinc-health"],
    queryFn: () => client.apiFetch<TincHealth>("/tinc/health"),
    refetchInterval: 15000,
    retry: false,
  });
  const nets = q.data?.networks ?? [];
  const failing = (q.data?.checks ?? []).filter((c) => !c.ok);
  return (
    <Section
      title="Network health"
      actions={
        q.data && (
          <StatusPill tone={q.data.status === "healthy" ? "ok" : q.data.status === "unknown" ? "neutral" : "bad"}>
            {q.data.status}
          </StatusPill>
        )
      }
      bodyClassName="p-0"
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="tinc health">
        {q.data?.networks_error && <NotMeasured why={q.data.networks_error} />}
        {q.data?.summary && (
          <div className="px-4 pt-3 text-[12.5px] text-muted">
            {q.data.summary.active} of {q.data.summary.total} network(s) active
            {q.data.summary.inactive > 0 && <> · <span className="text-warn">{q.data.summary.inactive} not running</span></>}
            {q.data.summary.unknown > 0 && <> · {q.data.summary.unknown} not measured</>}
          </div>
        )}
        {failing.length > 0 && (
          <ul className="space-y-0.5 px-4 pt-2 text-[12px]">
            {failing.map((c) => (
              <li key={c.name} className={c.unknown ? "text-muted" : "text-bad"}>
                <span className="font-mono">{c.name}</span> — {c.detail}
              </li>
            ))}
          </ul>
        )}
        <div className="divide-y divide-line">
          {nets.map((h) => (
            <div key={h.network} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-[12.5px]">
              <button
                type="button"
                className="font-mono font-medium hover:underline"
                onClick={() => onOpen?.(h.network)}
              >
                {h.network}
              </button>
              <span className="text-muted">{h.kind || "kind unknown"}</span>
              <UnitPill u={h} />
              {h.reach_error ? (
                <span className="text-muted" title={h.reach_error}>reach not measured</span>
              ) : (
                <span>
                  {n(h.reachable)}/{n(h.nodes)} nodes reachable
                  {(h.unreachable?.length ?? 0) > 0 && (
                    <span className="text-bad"> — unreachable: {(h.unreachable ?? []).join(", ")}</span>
                  )}
                </span>
              )}
              <span className="text-muted" title={h.connections_error}>
                {h.connections_error ? "connections not measured" : `${n(h.connections)} connection(s)`}
              </span>
            </div>
          ))}
        </div>
      </StateView>
    </Section>
  );
}

const nodeCols: DataListColumn<TincNode>[] = [
  {
    key: "name",
    label: "Node",
    render: (r) => (
      <span className="font-mono">
        {r.name}
        {r.self && <span className="ml-1 text-muted">(this node)</span>}
      </span>
    ),
    value: (r) => r.name,
  },
  {
    key: "reachable",
    label: "Reachable",
    render: (r) => <StatusPill tone={r.reachable ? "ok" : "bad"}>{r.reachable ? "yes" : "no"}</StatusPill>,
    value: (r) => (r.reachable ? 1 : 0),
  },
  { key: "host", label: "Address", render: (r) => <span className="font-mono">{r.self ? "—" : `${r.host}:${r.port}`}</span>, value: (r) => r.host },
  {
    key: "path",
    label: "Path",
    render: (r) =>
      r.self ? "—" : (
        <span className="text-[12px]">
          {r.udp_confirmed ? "UDP" : <span className="text-warn">TCP only</span>}
          {r.indirect && <span className="text-muted"> · via {r.via}</span>}
          {!r.valid_key && <span className="text-warn"> · no key</span>}
        </span>
      ),
    value: (r) => (r.udp_confirmed ? "udp" : "tcp"),
  },
  { key: "rtt_ms", label: "RTT", render: (r) => (r.rtt_ms ? `${r.rtt_ms} ms` : "—"), value: (r) => r.rtt_ms ?? null },
  { key: "pmtu", label: "PMTU", value: (r) => Number(r.pmtu) || null },
  { key: "protocol_minor", label: "Protocol", render: (r) => (r.protocol_minor ? `17.${r.protocol_minor}` : "—"), value: (r) => r.protocol_minor },
  { key: "rx_bytes", label: "Received", render: (r) => formatBytes(r.rx_bytes), value: (r) => r.rx_bytes },
  { key: "tx_bytes", label: "Sent", render: (r) => formatBytes(r.tx_bytes), value: (r) => r.tx_bytes },
];

const connCols: DataListColumn<TincConnection>[] = [
  { key: "node", label: "Peer", render: (r) => <span className="font-mono">{r.node}</span>, value: (r) => r.node },
  { key: "host", label: "Address", render: (r) => <span className="font-mono">{r.host}:{r.port}</span>, value: (r) => r.host },
  {
    key: "encrypted",
    label: "Keyed",
    render: (r) => <StatusPill tone={r.encrypted ? "ok" : "warn"}>{r.encrypted ? "yes" : "handshaking"}</StatusPill>,
    value: (r) => (r.encrypted ? 1 : 0),
  },
  { key: "mst", label: "Spanning tree", render: (r) => (r.mst ? "yes" : "no"), value: (r) => (r.mst ? 1 : 0) },
];

const edgeCols: DataListColumn<TincEdge>[] = [
  { key: "from", label: "From", render: (r) => <span className="font-mono">{r.from}</span>, value: (r) => r.from },
  { key: "to", label: "To", render: (r) => <span className="font-mono">{r.to}</span>, value: (r) => r.to },
  { key: "host", label: "At", render: (r) => <span className="font-mono">{r.host}:{r.port}</span>, value: (r) => r.host },
  { key: "weight", label: "Weight" },
];

const subnetCols: DataListColumn<TincSubnet>[] = [
  { key: "subnet", label: "Subnet", render: (r) => <span className="font-mono">{r.subnet}</span>, value: (r) => r.subnet },
  { key: "owner", label: "Owner", render: (r) => <span className="font-mono">{r.owner}</span>, value: (r) => r.owner },
];

function Table<T>({ title, rows, error, cols, rowKey, empty }: {
  title: string;
  rows: T[] | null;
  error?: string;
  cols: DataListColumn<T>[];
  rowKey: (r: T) => string;
  empty: string;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">
        {title} {rows ? `(${rows.length})` : ""}
      </div>
      {rows === null ? (
        <NotMeasured why={error} />
      ) : (
        <DataList items={rows} columns={cols} getRowKey={rowKey} emptyMessage={empty} />
      )}
    </div>
  );
}

const LOG_SIZES = [100, 200, 500, 2000];

/** One network, measured: nodes, connections, edges, subnets, traffic, and the journal tail. */
export function TincNetworkStatus({ net }: { net: string }) {
  const { client } = useAuth();
  const [lines, setLines] = React.useState(200);
  const q = useQuery({
    queryKey: ["tinc-status", net],
    queryFn: () => client.apiFetch<NetworkStatus>(`/tinc/networks/${encodeURIComponent(net)}/status`),
    refetchInterval: 10000,
    retry: false,
  });
  const log = useQuery({
    queryKey: ["tinc-log", net, lines],
    queryFn: () =>
      client.apiFetch<{ lines: string[] | null; unit: string }>(`/tinc/networks/${encodeURIComponent(net)}/log?lines=${lines}`),
    refetchInterval: 15000,
    retry: false,
  });
  const s = q.data;
  return (
    <div className="mt-4 space-y-4">
      <StateView loading={q.isLoading} error={q.error} errorContext="the network's status">
        {s && (
          <>
            <div className="flex flex-wrap items-center gap-3 text-[12.5px]">
              <UnitPill u={s} />
              {s.state_error && <span className="text-muted">{s.state_error}</span>}
              {s.self && <span className="text-muted">this node is <span className="font-mono">{s.self}</span></span>}
              {s.traffic ? (
                <span>
                  traffic since start: {formatBytes(s.traffic.rx_bytes)} received · {formatBytes(s.traffic.tx_bytes)} sent
                </span>
              ) : (
                <span className="text-muted">traffic not measured</span>
              )}
            </div>
            <Table title="Nodes" rows={s.nodes} error={s.nodes_error} cols={nodeCols} rowKey={(r) => r.name} empty="tinc knows no nodes." />
            <Table
              title="Live connections"
              rows={s.connections}
              error={s.connections_error}
              cols={connCols}
              rowKey={(r) => `${r.node}-${r.socket}`}
              empty="No live meta connection — this node reaches no peer directly."
            />
            <div className="grid gap-4 lg:grid-cols-2">
              <Table title="Edges" rows={s.edges} error={s.edges_error} cols={edgeCols} rowKey={(r) => `${r.from}>${r.to}`} empty="No edges." />
              <Table title="Subnets" rows={s.subnets} error={s.subnets_error} cols={subnetCols} rowKey={(r) => `${r.subnet}@${r.owner}`} empty="No subnets advertised." />
            </div>
          </>
        )}
      </StateView>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted">Log ({log.data?.unit ?? `tinc@${net}`})</span>
          <select
            className="rounded border border-line bg-bg px-2 py-0.5 text-[12px]"
            value={lines}
            onChange={(e) => setLines(Number(e.target.value))}
          >
            {LOG_SIZES.map((v) => (
              <option key={v} value={v}>last {v} lines</option>
            ))}
          </select>
        </div>
        <StateView loading={log.isLoading} error={log.error} errorContext="the network's journal">
          {/* tincd's own lines as they are; `ozpanel tinc-wait-bind` (the unit's start gate) as the
              one log line — the same view every journal in the console gets. */}
          <LogLines rows={log.data?.lines} heightClass="max-h-96" empty="The journal holds no lines for this unit." />
        </StateView>
      </div>
    </div>
  );
}
